/**
 * Live mode — GPS tracking, current-location marker, nearest-route-point indicator,
 * and route progress display.
 */

import L from "leaflet";
import type { Map, LayerGroup } from "leaflet";
import type { RouteContext } from "./routing";
import { haversine } from "./geo";
import { distanceAlongRoute, elapsedTime, pointAtDistance, remainingTime } from "./live/remainingTime";
import { formatDuration } from "./poi/types";
import {
  liveModeEl,
  liveProgressEl,
  liveGpsFreshnessEl,
  setLiveMode,
  setLiveProgress,
} from "./dom";
import type { ParsedPoi } from "./poi/types";

// ─── State ───────────────────────────────────────────────────────────────────

let watchId:          number | null    = null;
let liveLayerGroup:   L.LayerGroup     = L.layerGroup();
let carMarker:        L.Marker | null  = null;
let accuracyCircle:   L.Circle | null  = null;
let nearestDot:       L.CircleMarker | null = null;
let connectorLine:    L.Polyline | null = null;

let _map:          Map | null             = null;
let _routeCtx:     RouteContext | null       = null;

/** Seconds since trip departure along the route (0 = not started). */
let _elapsedS:      number                = 0;

/** Timestamp (ms) of the most recent successful GPS fix. */
let _positionTsMs:     number                = 0;
let _freshnessInterval: ReturnType<typeof setInterval> | null = null;

/** Exposed for index.ts to compute live ETAs and refresh POI list/markers. */
let _onPositionUpdate: ((elapsedS: number) => void) | null = null;

// ─── Public API ───────────────────────────────────────────────────────────────

export function liveStart(map: Map, routeCtx: RouteContext): void {
  if (watchId !== null) return; // already running
  _map      = map;
  _routeCtx = routeCtx;
  _elapsedS = 0;
  _positionTsMs = 0;

  liveLayerGroup.addTo(map);

  // One-shot permission check
  if (!navigator.geolocation) {
    showError("Lokalizacja niedostępna w tej przeglądarce.");
    setLiveMode(false);
    return;
  }

  navigator.geolocation.getCurrentPosition(
    () => {
      // Permission granted — start watching
      watchId = navigator.geolocation!.watchPosition(
        (pos) => onEachPosition(pos, routeCtx, map),
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            showError("Lokalizacja niedostępna — sprawdź uprawnienia przeglądarki.");
            liveStop();
            setLiveMode(false);
          }
          // POSITION_UNAVAILABLE / TIMEOUT — silently retry via watchPosition
        },
      );
      startGpsFreshnessTimer();
    },
    (err) => {
      if (err.code === err.PERMISSION_DENIED) {
        showError("Lokalizacja niedostępna — sprawdź uprawnienia przeglądarki.");
        setLiveMode(false);
      }
    },
    { enableHighAccuracy: true },
  );
}

export function liveStop(): void {
  if (watchId !== null) {
    navigator.geolocation?.clearWatch(watchId);
    watchId = null;
  }
  liveLayerGroup.clearLayers();
  carMarker      = null;
  accuracyCircle = null;
  nearestDot     = null;
  connectorLine  = null;
  _map           = null;
  _routeCtx      = null;
  _elapsedS      = 0;
  _positionTsMs = 0;
  _onPositionUpdate = null;
  stopGpsFreshnessTimer();
  setLiveProgress("");
  liveGpsFreshnessEl.textContent = "";
}

// ─── Per-position handler ─────────────────────────────────────────────────────

function onEachPosition(
  pos: GeolocationPosition,
  routeCtx: RouteContext,
  map: Map,
): void {
  const { latitude: lat, longitude: lng, accuracy } = pos.coords;

  _positionTsMs = Date.now();

  // 1. Car marker
  carMarker = updateCarMarker(lat, lng, pos.coords.heading ?? null, carMarker);

  // 2. Accuracy circle (capped at 500 m)
  accuracyCircle = updateAccuracyCircle(lat, lng, accuracy, accuracyCircle);

  // 3. Nearest point on route
  const nearest = nearestPointOnRoute(lat, lng, routeCtx);
  const distToRoute = haversine({ lat, lng }, nearest.pt);

  if (distToRoute > 50) {
    // Show blue dot + connector line
    nearestDot      = updateNearestDot(nearest.pt, nearestDot);
    connectorLine   = updateConnectorLine({ lat, lng }, nearest.pt, connectorLine);
  } else {
    // On route — remove dot and line
    if (nearestDot)    { liveLayerGroup.removeLayer(nearestDot);    nearestDot    = null; }
    if (connectorLine) { liveLayerGroup.removeLayer(connectorLine); connectorLine = null; }
  }

  // 4. Elapsed time since trip departure (per-segment, mirrors remainingTime)
  _elapsedS = elapsedTime(nearest.distFromStart, routeCtx);

  // 5. Progress text
  updateProgress(nearest.distFromStart, routeCtx);

  // 6. Notify index.ts to refresh POI ETAs
  _onPositionUpdate?.(_elapsedS);
}

// ─── Map element updaters ─────────────────────────────────────────────────────

const CAR_ICON = L.divIcon({
  className: "",
  html: `<div id="live-car" style="font-size:22px;line-height:1;text-shadow:0 1px 3px rgba(0,0,0,0.5);">🚗</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function updateCarMarker(
  lat: number,
  lng: number,
  heading: number | null,
  existing: L.Marker | null,
): L.Marker {
  const icon = heading !== null && heading !== undefined
    ? L.divIcon({
        className: "",
        html: `<div id="live-car" style="font-size:22px;line-height:1;text-shadow:0 1px 3px rgba(0,0,0,0.5);transform:rotate(${heading}deg);display:inline-block;">🚗</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      })
    : CAR_ICON;

  if (existing) {
    existing.setLatLng([lat, lng]);
    existing.setIcon(icon);
    return existing;
  }
  return L.marker([lat, lng], { icon, zIndexOffset: 2000 }).addTo(liveLayerGroup);
}

function updateAccuracyCircle(
  lat: number,
  lng: number,
  accuracy: number,
  existing: L.Circle | null,
): L.Circle {
  const radius = Math.min(accuracy, 500);
  if (existing) {
    existing.setLatLng([lat, lng]);
    existing.setRadius(radius);
    return existing;
  }
  return L.circle([lat, lng], {
    radius,
    color: "#3b82f6",
    fillColor: "#3b82f6",
    fillOpacity: 0.15,
    weight: 1,
    opacity: 0.4,
    interactive: false,
  }).addTo(liveLayerGroup);
}

function updateNearestDot(pt: { lat: number; lng: number }, existing: L.CircleMarker | null): L.CircleMarker {
  if (existing) {
    existing.setLatLng([pt.lat, pt.lng]);
    return existing;
  }
  return L.circleMarker([pt.lat, pt.lng], {
    radius: 6,
    color: "#3b82f6",
    fillColor: "#3b82f6",
    fillOpacity: 0.7,
    weight: 2,
    opacity: 1,
    interactive: false,
    zIndexOffset: 1990,
  }).addTo(liveLayerGroup);
}

function updateConnectorLine(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  existing: L.Polyline | null,
): L.Polyline {
  if (existing) {
    existing.setLatLngs([[from.lat, from.lng], [to.lat, to.lng]]);
    return existing;
  }
  return L.polyline([[from.lat, from.lng], [to.lat, to.lng]], {
    color: "#94a3b8",
    weight: 2,
    opacity: 0.6,
    dashArray: "4 4",
    interactive: false,
  }).addTo(liveLayerGroup);
}

// ─── Progress ─────────────────────────────────────────────────────────────────

function updateProgress(distFromStart: number, routeCtx: RouteContext): void {
  const pct = Math.min(100, Math.round((distFromStart / routeCtx.totalDistM) * 100));
  const secs = remainingTime(distFromStart, routeCtx);
  const t1 = formatDuration(secs);
  setLiveProgress(`Pokonano ${pct}% trasy — pozostało ~${t1}`);
}

// ─── GPS freshness timer ─────────────────────────────────────────────────────

function startGpsFreshnessTimer(): void {
  stopGpsFreshnessTimer();
  _freshnessInterval = setInterval(() => {
    if (_positionTsMs === 0) return;
    const delayS = Math.round((Date.now() - _positionTsMs) / 1000);
    liveGpsFreshnessEl.textContent = `opóźnienie GPS: ${delayS}s`;
  }, 1000);
}

function stopGpsFreshnessTimer(): void {
  if (_freshnessInterval !== null) {
    clearInterval(_freshnessInterval);
    _freshnessInterval = null;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Find the nearest point on the route polyline to the given GPS coordinates. */
function nearestPointOnRoute(
  lat: number,
  lng: number,
  routeCtx: RouteContext,
): { pt: { lat: number; lng: number }; distFromStart: number } {
  const pt = { lat, lng };
  const distFromStart = distanceAlongRoute(pt, routeCtx.geometry);
  const nearest = pointAtDistance(distFromStart, routeCtx.geometry);
  return { pt: nearest.pt, distFromStart: nearest.distFromStart };
}

function showError(msg: string): void {
  setLiveProgress(msg);
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function onLivePositionUpdate(
  fn: (elapsedS: number) => void,
): void {
  _onPositionUpdate = fn;
  if (isLiveRunning()) {
    // Session-restore auto-start: liveStart already ran, fire immediately
    fn(_elapsedS);
  }
}

export function getLiveElapsedS(): number {
  return _elapsedS;
}

export function getLastPositionAgeS(): number {
  return Math.round((Date.now() - _positionTsMs) / 1000);
}

export function clearLivePoiEtas(pois: ParsedPoi[]): void {
  for (const p of pois) { p.liveEtaS = undefined; p.liveMissed = undefined; }
}

export function isLiveRunning(): boolean {
  return watchId !== null;
}

export { liveLayerGroup };